/**
 * Date Field Type - Inline Renderers
 *
 * Read-only and editable in-table cell renderers for date columns.
 */

import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { format } from 'date-fns';
import { t } from '@lingui/macro';
import { TableTooltip } from '../../TableTooltip';
import { DatePickerCell } from './DatePickerCell';
import { Text } from 'components/text';
import { CellContainer } from '../../tableStyles';
import type { ColumnRenderer, RenderOptions, CustomColumn } from '../../columnModel';
import { getCellDateValue } from '../cellValue';

// Default date formatter
const defaultDateFormatter = (dateString: string | null): string => {
  if (!dateString) return '';
  try {
    return format(new Date(dateString), 'dd-MMM-yyyy');
  } catch {
    return t`Invalid date`;
  }
};

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Date read-only renderer - displays formatted date in a cell container
 * Uses getValue() to support nested accessors
 */
export const dateReadOnlyRenderer = <T extends { id: number | string }>(
  _col: CustomColumn<T>,
  options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const dateFormatter = options.dateFormatter ?? defaultDateFormatter;

  // Helper to get tooltip text for a date value
  const getTooltipText = (value: unknown): string => {
    if (!value) return t` `;
    try {
      return format(new Date(value as string), 'dd-MMM-yyyy');
    } catch {
      return String(value);
    }
  };

  return {
    cell: (cellContext: CellContext<T, unknown>) => {
      const value = cellContext.getValue();
      const tooltipText = getTooltipText(value);

      return (
        <TableTooltip title={tooltipText}>
          <CellContainer>
            <Text variant="bodyXs">{dateFormatter(value as string | null)}</Text>
          </CellContainer>
        </TableTooltip>
      );
    },
    tooltipRender: (cell: CellContext<T, unknown>) => {
      const value = cell.getValue();
      return getTooltipText(value);
    },
  };
};

// ============================================================================
// Editable Column Renderer
// ============================================================================

/**
 * Date editable renderer - unified pattern
 * Uses col.id as the data field, auto-infers update
 * Supports conditional editability via editable: (cell) => boolean
 */
export const dateEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const editableProp = col.editable;
  const dateFormatter = options.dateFormatter ?? defaultDateFormatter;

  return {
    cell: (context: CellContext<T, unknown>) => {
      const value = getCellDateValue(context, col.id);
      const isRowEditable =
        typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

      if (!isRowEditable) {
        return (
          <CellContainer>
            <Text variant="bodyXs">{dateFormatter(value)}</Text>
          </CellContainer>
        );
      }

      return (
        <DatePickerCell
          context={context}
          value={value}
          onUpdate={newDate => {
            if (col.onUpdate) {
              return col.onUpdate(context.row.original, {
                [col.id]: newDate,
              } as Partial<T>);
            }
          }}
          required={col.required}
        />
      );
    },
    meta: {
      editable: true,
    },
    tooltipRender: (cell: CellContext<T, unknown>) => {
      const value = cell.getValue();
      if (!value) return t` `;
      try {
        return format(new Date(value as string), 'dd-MMM-yyyy');
      } catch {
        return String(value);
      }
    },
  };
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const dateReadOnly: ColumnRenderer = dateReadOnlyRenderer;

/** Editable renderer with proper typing */
export const dateEditable: ColumnRenderer = dateEditableRenderer;
